/**
 * The year a class is in, in the notation the Fédération Wallonie-Bruxelles
 * uses: M1-M3 for maternelle, P1-P6 for primaire.
 *
 * Stored on `Settings.className`, which used to be free text. It drives which
 * référentiel the lesson form offers, so it has to be a value the curriculum
 * data can be keyed on rather than whatever was typed.
 */
export const LEVELS = ['M1', 'M2', 'M3', 'P1', 'P2', 'P3', 'P4', 'P5', 'P6'] as const;

export type Level = (typeof LEVELS)[number];

export const DEFAULT_LEVEL: Level = 'P3';

/** Long form for the year picker, so the list reads as more than a code. */
export const LEVEL_LABELS: Readonly<Record<Level, string>> = {
  M1: 'M1 — 1re maternelle',
  M2: 'M2 — 2e maternelle',
  M3: 'M3 — 3e maternelle',
  P1: 'P1 — 1re primaire',
  P2: 'P2 — 2e primaire',
  P3: 'P3 — 3e primaire',
  P4: 'P4 — 4e primaire',
  P5: 'P5 — 5e primaire',
  P6: 'P6 — 6e primaire',
};

const WRITTEN_YEAR = /(\d)\s*(?:re|ère|e|ème)?\s*(primaire|maternelle)/i;

/**
 * Coerce whatever is on disk into a level.
 *
 * The field was free text before, so saved data can hold "P3", "P3A",
 * "3e primaire" or something else entirely. Anything unrecognisable falls back
 * to the default rather than leaving the picker on an impossible value.
 */
export function asLevel(value: unknown): Level {
  if (typeof value !== 'string') return DEFAULT_LEVEL;
  const trimmed = value.trim();

  const known = (candidate: string): Level | null =>
    (LEVELS as readonly string[]).includes(candidate) ? (candidate as Level) : null;

  const code = /^([MP])\s*([1-6])/.exec(trimmed.toUpperCase());
  if (code) {
    const candidate = known(`${code[1]}${code[2]}`);
    if (candidate) return candidate;
  }

  const written = WRITTEN_YEAR.exec(trimmed);
  if (written) {
    const candidate = known(`${written[2]?.toLowerCase().startsWith('mat') ? 'M' : 'P'}${written[1]}`);
    if (candidate) return candidate;
  }

  return DEFAULT_LEVEL;
}
